import React, { useEffect } from 'react'
import { useHistory, useLocation } from 'react-router-dom'

import {
    Chip,
    CircularProgress,
    Tab,
    Tabs,
    Typography,
    makeStyles,
    Theme,
} from '@material-ui/core'

import {
    RecordsPersonUnit,
    RecordsTimelineEvent,
    useRecordsPersonFile,
    useRecordsPersonTimeline,
} from '../../hooks/useRecords'
import {
    mergeRecordsSearch,
    parsePersonFileTab,
    parseRecordsSearch,
    PersonFileTab,
    recordsPersonPath,
    recordsUnitPath,
} from './paths'
import { PersonNotes } from './PersonNotes'
import { TrailEventCard, TrailEventList } from './TrailEventCard'
import { TrailExport } from './TrailExport'
import { TrailKindFilter } from './TrailKindFilter'
import {
    eventMatchesKindKeys,
    LEASING_KINDS,
    parseKindKeys,
    REQUEST_KINDS,
    serializeAuditExportKinds,
    serializeKindKeys,
} from './trailKinds'

interface PersonFileProps {
    userId?: number | null
    leadId?: number | null
}

const LEASE_KINDS = new Set(['lease'])

const useStyles = makeStyles((theme: Theme) => ({
    root: {
        display: 'flex',
        flexDirection: 'column',
        minHeight: 0,
        height: '100%',
        overflow: 'hidden',
    },
    header: {
        padding: theme.spacing(3),
        backgroundColor: theme.palette.background.paper,
        boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
        flexShrink: 0,
        textAlign: 'left',
    },
    name: {
        fontWeight: 700,
    },
    meta: {
        color: theme.palette.text.secondary,
        marginTop: theme.spacing(0.5),
    },
    chips: {
        display: 'flex',
        flexWrap: 'wrap',
        gap: theme.spacing(0.75),
        marginTop: theme.spacing(1.5),
    },
    tabs: {
        backgroundColor: theme.palette.background.paper,
        borderBottom: `1px solid ${theme.palette.divider}`,
        flexShrink: 0,
        minHeight: 44,
    },
    tab: {
        minHeight: 44,
        textTransform: 'none',
        fontWeight: 600,
    },
    listWrap: {
        flex: 1,
        minHeight: 0,
        overflow: 'auto',
        padding: theme.spacing(1, 2, 3),
    },
    loading: {
        display: 'flex',
        justifyContent: 'center',
        padding: theme.spacing(6),
    },
    empty: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: theme.spacing(6),
        textAlign: 'center',
        color: theme.palette.text.secondary,
    },
    trailBar: {
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'space-between',
        flexWrap: 'wrap',
        flexShrink: 0,
    },
}))

const eventMatchesTab = (event: RecordsTimelineEvent, tab: PersonFileTab) => {
    if (tab === 'timeline') return event.kind !== 'lease'
    if (tab === 'leases') return LEASE_KINDS.has(event.kind)
    if (tab === 'requests') return REQUEST_KINDS.has(event.kind)
    if (tab === 'leasing') return LEASING_KINDS.has(event.kind)
    return false
}

const emptyCopy = (tab: PersonFileTab) => {
    if (tab === 'leases') return 'No leases on file for this person.'
    if (tab === 'requests')
        return 'No service requests on file for this person.'
    if (tab === 'leasing') return 'No leasing activity linked to this person.'
    return 'Nothing on the audit trail for this person yet.'
}

const unitChipLabel = (unit: RecordsPersonUnit) => {
    const name = unit.unit_name || 'Unit'
    if (unit.area_label) return `${name} · ${unit.area_label}`
    return name
}

const statusLabel = (status: string | undefined) => {
    if (status === 'current') return 'Current resident'
    if (status === 'future') return 'Future resident'
    if (status === 'incoming') return 'Incoming'
    if (status === 'cancelled') return 'Cancelled'
    if (status === 'past' || status === 'former') return 'Former resident'
    return status || ''
}

export const PersonFile = ({
    userId = null,
    leadId = null,
}: PersonFileProps) => {
    const classes = useStyles()
    const history = useHistory()
    const location = useLocation()
    const search = parseRecordsSearch(location.search)
    const tab = parsePersonFileTab(search.tab)
    const selectedKinds = parseKindKeys(search.kinds)

    const setTab = (next: PersonFileTab) => {
        history.replace({
            pathname: location.pathname,
            search: mergeRecordsSearch(
                location.search,
                { tab: next },
                next === 'timeline' ? [] : ['kinds'],
            ),
        })
    }

    const setKinds = (next: string[]) => {
        history.replace({
            pathname: location.pathname,
            search: mergeRecordsSearch(location.search, {
                kinds: serializeKindKeys(parseKindKeys(next.join(','))),
            }),
        })
    }

    const fileQuery = useRecordsPersonFile(userId, leadId)
    const timelineQuery = useRecordsPersonTimeline(userId, leadId)

    useEffect(() => {
        const person = fileQuery.data
        if (userId != null || person?.user_id == null) return
        history.replace({
            pathname: recordsPersonPath(person.user_id),
            search: location.search,
        })
    }, [fileQuery.data, history, location.search, userId])
    const person = fileQuery.data
    const tabEvents = (timelineQuery.data?.results ?? []).filter((event) =>
        eventMatchesTab(event, tab),
    )
    const events =
        tab === 'timeline'
            ? tabEvents.filter((event) =>
                  eventMatchesKindKeys(event.kind, selectedKinds),
              )
            : tabEvents
    const loading = fileQuery.isLoading || timelineQuery.isLoading
    const missing = fileQuery.isError || timelineQuery.isError

    const goUnit = (unitId: number | null) => {
        if (unitId != null) {
            history.push({
                pathname: recordsUnitPath(unitId),
                search: mergeRecordsSearch(location.search, {}, [
                    'tab',
                    'category',
                    'open',
                    'kinds',
                ]),
            })
        }
    }

    return (
        <div className={classes.root}>
            <div className={classes.header}>
                {person ? (
                    <>
                        <Typography variant="h5" className={classes.name}>
                            {person.name || 'Person File'}
                        </Typography>
                        <Typography variant="body2" className={classes.meta}>
                            {[
                                statusLabel(person.status),
                                person.email,
                                person.phone,
                                `${
                                    person.open_service_request_count
                                } open request${
                                    person.open_service_request_count === 1
                                        ? ''
                                        : 's'
                                }`,
                            ]
                                .filter(Boolean)
                                .join(' · ')}
                        </Typography>
                        {person.current_units.length > 0 ||
                        (person.future_units &&
                            person.future_units.length > 0) ||
                        person.past_units.length > 0 ? (
                            <div className={classes.chips}>
                                {person.current_units.map((unit) => (
                                    <Chip
                                        key={`current-${unit.lease_id}`}
                                        size="small"
                                        color="primary"
                                        clickable={unit.unit_id != null}
                                        label={unitChipLabel(unit)}
                                        onClick={() => goUnit(unit.unit_id)}
                                        style={{ fontWeight: 600 }}
                                    />
                                ))}
                                {(person.future_units ?? []).map((unit) => (
                                    <Chip
                                        key={`future-${unit.lease_id}`}
                                        size="small"
                                        clickable={unit.unit_id != null}
                                        label={`Future · ${unitChipLabel(
                                            unit,
                                        )}`}
                                        onClick={() => goUnit(unit.unit_id)}
                                        style={{ fontWeight: 600 }}
                                    />
                                ))}
                                {person.past_units.map((unit) => (
                                    <Chip
                                        key={`past-${unit.lease_id}`}
                                        size="small"
                                        clickable={unit.unit_id != null}
                                        label={`Former · ${unitChipLabel(
                                            unit,
                                        )}`}
                                        onClick={() => goUnit(unit.unit_id)}
                                        style={{ fontWeight: 600 }}
                                    />
                                ))}
                            </div>
                        ) : (
                            <Typography
                                variant="body2"
                                className={classes.meta}
                            >
                                No unit on file
                            </Typography>
                        )}
                    </>
                ) : missing ? (
                    <>
                        <Typography variant="h5" className={classes.name}>
                            Person not found
                        </Typography>
                        <Typography variant="body2" className={classes.meta}>
                            This person is not associated with this property.
                        </Typography>
                    </>
                ) : (
                    <Typography variant="h5" className={classes.name}>
                        Person File
                    </Typography>
                )}
            </div>
            <Tabs
                className={classes.tabs}
                value={tab}
                onChange={(_event, next: PersonFileTab) => setTab(next)}
                indicatorColor="primary"
                textColor="primary"
                variant="scrollable"
            >
                <Tab
                    className={classes.tab}
                    value="timeline"
                    label="Audit Trail"
                />
                <Tab className={classes.tab} value="leases" label="Leases" />
                <Tab
                    className={classes.tab}
                    value="requests"
                    label="Service Requests"
                />
                <Tab className={classes.tab} value="leasing" label="Leasing" />
                <Tab className={classes.tab} value="notes" label="Notes" />
            </Tabs>
            {tab === 'timeline' && !missing ? (
                <div className={classes.trailBar}>
                    {!loading ? (
                        <TrailKindFilter
                            eventKinds={tabEvents.map((event) => event.kind)}
                            selected={selectedKinds}
                            onChange={setKinds}
                        />
                    ) : null}
                    <TrailExport
                        userId={userId}
                        leadId={leadId}
                        kinds={serializeAuditExportKinds(selectedKinds)}
                    />
                </div>
            ) : null}
            <div className={classes.listWrap}>
                {tab === 'notes' ? (
                    missing ? (
                        <div className={classes.empty}>
                            Search for someone associated with this property.
                        </div>
                    ) : (
                        <PersonNotes userId={userId} leadId={leadId} />
                    )
                ) : loading ? (
                    <div className={classes.loading}>
                        <CircularProgress size={28} />
                    </div>
                ) : missing ? (
                    <div className={classes.empty}>
                        Search for someone associated with this property.
                    </div>
                ) : events.length === 0 ? (
                    <div className={classes.empty}>
                        {tab === 'timeline' && selectedKinds.length > 0
                            ? 'No events of this kind.'
                            : emptyCopy(tab)}
                    </div>
                ) : (
                    <TrailEventList>
                        {events.map((event) => (
                            <TrailEventCard
                                key={`${event.kind}-${event.source}-${event.source_id}-${event.at}`}
                                event={event}
                                onClick={
                                    event.unit_id != null
                                        ? () => goUnit(event.unit_id)
                                        : undefined
                                }
                            />
                        ))}
                    </TrailEventList>
                )}
            </div>
        </div>
    )
}
